// src/lib/fishiq/fishIQStructureRead.ts
// Structure is derived from water/map evidence only. Species rankings are not
// allowed to create structure evidence and then inflate their own confidence.

import type { FishIQWaterProfile } from '../../types';

const clean = (value: any) => String(value ?? '').trim();
const lower = (value: any) => clean(value).toLowerCase();
const unique = (items: string[]) => Array.from(new Set(items.map(clean).filter(Boolean)));

export type FishIQStructureReadInput = {
  profile: FishIQWaterProfile;
  species?: any[] | null; // retained for call compatibility; intentionally unused
  waterFeatures?: any[] | null;
  environment?: any | null;
};

export function buildFishIQStructureRead(input: FishIQStructureReadInput): string[] {
  const profile = input.profile;
  const featureText = (Array.isArray(input.waterFeatures) ? input.waterFeatures : [])
    .map((item) => [item?.name, item?.type, item?.kind, item?.waterKind, item?.waterType, item?.tags?.waterway, item?.tags?.natural]
      .map(lower)
      .join(' '))
    .join(' ');
  const hay = [profile.waterName, profile.waterKind, profile.waterType, profile.regionHint, featureText]
    .map(lower)
    .filter(Boolean)
    .join(' ');

  const tags: string[] = [];
  if (/\b(river|creek|stream|brook|waterway|canal)\b/.test(hay)) tags.push('current seams', 'bends', 'eddies');
  if (/\b(lake|reservoir|pond)\b/.test(hay)) tags.push('points', 'drop-offs', 'weed edges');
  if (/\b(surf|beach)\b/.test(hay)) tags.push('troughs', 'cuts', 'sandbars');
  if (/\b(bay|harbor|harbour|marina|estuary|lagoon|inlet)\b/.test(hay)) tags.push('channels', 'dock shade', 'mouths');
  if (/\b(reef|rock|kelp|jetty|breakwater|pier)\b/.test(hay)) tags.push('reef edges', 'rock transitions', 'current breaks');
  if (/\b(marsh|mangrove|grass|eelgrass|vegetation|weed)\b/.test(hay)) tags.push('grass edges', 'shoreline cover');

  const bathymetry = input.environment?.bathymetry?.available === true
    ? input.environment.bathymetry
    : null;
  if (bathymetry) {
    const structureTypes = Array.isArray(bathymetry?.structureTypes)
      ? bathymetry.structureTypes
      : [];
    for (const raw of structureTypes) {
      const value = lower(raw).replace(/[_-]+/g, ' ').trim();
      if (!value || value === 'unknown') continue;
      if (/ridge/.test(value)) tags.push('ridge');
      else if (/ledge|drop off|dropoff|break/.test(value)) tags.push('ledge', 'drop-off');
      else if (/channel/.test(value)) tags.push('channel');
      else if (/basin edge/.test(value)) tags.push('basin edge');
      else if (/flat/.test(value)) tags.push('flat');
      else tags.push(value);
    }

    const relief = Number(bathymetry?.localReliefMeters);
    if (Number.isFinite(relief) && relief >= 20) tags.push('high-relief edge');
    else if (Number.isFinite(relief) && relief >= 8) tags.push('break line');

    const slopeClass = lower(bathymetry?.slopeClass);
    if (/steep/.test(slopeClass)) tags.push('steep break');
    else if (/moderate/.test(slopeClass)) tags.push('moderate break');
  }

  // Unknown water should remain unknown instead of receiving invented structure.
  return unique(tags).slice(0, 10);
}
